import { useCallback, useMemo } from 'react'

import type { ReleaseForm, TaggableUser } from '@api'
import times from 'lodash/times'
import { type ListChildComponentProps, VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content'
import { TextField } from '@/components/ui/text-field'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs.ts'
import { ManageSelectedUsers } from '@/modules/chatting/ui/components/ManageSelectedUsers.tsx'
import { FilterSortOrderOptions } from '@/modules/chatting/ui/components/filter-sort-order-options.tsx'

import { useInfiniteScroll } from '../useInfiniteScroll.ts'
import { getTaggableItemsListItemSize } from '../utils.ts'
import { TaggableCreatorSkeleton } from './TaggableCreatorSkeleton'
import { TaggableCreatorsItem } from './TaggableCreatorsItem'
import { useTagUserData } from './useTagUserData.ts'

interface Props {
  onClose: () => void
  onClickDone: () => void
  selectedReleaseForms: ReleaseForm[]
  selectedCreators: TaggableUser[]
  onChangeCreatorSelection: (creator: TaggableUser, state: boolean) => void
  onResetSelection: () => void
}

export const TagUserTabContent = ({
  onClose,
  onClickDone,
  selectedCreators,
  onChangeCreatorSelection,
  onResetSelection,
  selectedReleaseForms,
}: Props) => {
  const {
    handleSearchChange,
    taggableUsers,
    isLoading,
    canLoadMore,
    onLoadMore,
    isLoadingMore,
    filterSortOrderOptions,
    toggleShow,
  } = useTagUserData()

  const { lastVisibleIndexRef, container, onScroll } = useInfiniteScroll({
    canLoadMore,
    onLoadMore,
    entities: taggableUsers,
  })

  const renderCreatorItem: React.ComponentType<
    ListChildComponentProps<(TaggableUser & { isSelected?: boolean })[]>
  > = useCallback(
    ({ data, index, style }) => {
      const creator = data[index]
      const { id, hiddenForRf, type } = creator

      lastVisibleIndexRef.current = index

      return (
        <TaggableCreatorsItem
          onChangeSelected={(state) => onChangeCreatorSelection(creator, state)}
          isSelected={!!creator.isSelected}
          taggable={creator}
          style={style}
          onToggleShow={() => toggleShow({ id, type, hide: !hiddenForRf })}
        />
      )
    },
    [lastVisibleIndexRef, onChangeCreatorSelection, toggleShow],
  )

  const handleClickInvite = () => {
    openDialog({ type: DIALOG_TYPE.INVITE_TAGGABLE_CREATOR })
  }

  // We use it to prevent unnecessary re-renders of the whole list
  const usersToRender = useMemo(
    () =>
      taggableUsers.map((user) => {
        const isSelected = selectedCreators.find(
          (selectedCreator) => selectedCreator.id === user.id,
        )

        return isSelected ? { ...user, isSelected: true } : user
      }),
    [taggableUsers, selectedCreators],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <TextField
          placeholder="Search"
          className="border-border-secondary flex-1 resize-none"
          prefix={<SearchIcon className="text-title" />}
          onChange={handleSearchChange}
        />
        <FilterSortOrderOptions {...filterSortOrderOptions} />
      </div>
      <div
        className="no-scrollbar border-border-secondary bg-card relative max-h-[532px] w-full flex-1 overflow-hidden rounded-lg border py-4"
        ref={container.ref}
      >
        {isLoading ? (
          times(6).map((num) => <TaggableCreatorSkeleton key={num} />)
        ) : taggableUsers.length === 0 ? (
          <EmptyTableContent>
            No results found. <br />
            Please change filter criteria.
          </EmptyTableContent>
        ) : (
          <VariableSizeList
            className="scrollbar-hide min-h-[498px] max-w-full overflow-hidden"
            itemData={usersToRender}
            itemSize={getTaggableItemsListItemSize}
            height={container.height}
            itemCount={taggableUsers?.length ?? 0}
            width={container.width}
            onScroll={onScroll}
          >
            {renderCreatorItem}
          </VariableSizeList>
        )}
        <div className="bg-card absolute bottom-0 left-0 right-0 space-y-4">
          <ManageSelectedUsers
            className="mx-4 mb-4"
            onCancel={onResetSelection}
            selectedCount={
              selectedCreators.length + selectedReleaseForms.length
            }
          />
          {isLoadingMore && (
            <div>
              <IconLoading className="mx-auto my-3 animate-spin" />
            </div>
          )}
        </div>
      </div>
      <Dialog.ClosableFooter
        className="flex"
        onClose={onClose}
        buttonClassName="flex-1 min-w-max"
      >
        <Button
          variant="outline"
          onClick={handleClickInvite}
          className="min-w-max flex-1"
        >
          Invite New User
        </Button>
        <Button
          onClick={onClickDone}
          className="min-w-max flex-1"
        >
          Done
        </Button>
      </Dialog.ClosableFooter>
    </div>
  )
}
